import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  Autocomplete,
  AutocompleteProps,
  Option,
  Typography,
} from '@beeline/design-system-react/extracted-styles';

import { Floor } from 'src/api/types';
import { getFloorsState } from 'src/store/floors/selectors';
import { fetchFloors } from 'src/store/floors/services';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { truphy } from 'src/utils/truphy';

import { TooltipWrapper } from '../TooltipWrapper/TooltipWrapper';

export type FloorSelectItem = Option<Floor>;

export interface FloorSelectProps
  extends Omit<
    AutocompleteProps<Floor>,
    | 'label'
    | 'size'
    | 'fullWidth'
    | 'placeholder'
    | 'noOptionsText'
    | 'options'
    | 'loading'
    | 'renderValue'
    | 'multiple'
    | 'values'
    | 'onChange'
    | 'value'
    | 'type'
    | 'onInputChange'
    | 'onInputClear'
  > {
  buildingId?: number;
  floor: FloorSelectItem | undefined;
  onChange: (value: FloorSelectItem | undefined) => void;
  filterOptions?: (option: FloorSelectItem) => boolean;
}

const renderValue = (floor: FloorSelectItem | null): string =>
  floor?.value.title ?? '';

const defaultMakeOption = (option: FloorSelectItem | null): JSX.Element => {
  return option ? (
    <TooltipWrapper title={option.value.title}>
      <Typography variant='body2' ellipsis>
        {option.value.title}
      </Typography>
    </TooltipWrapper>
  ) : (
    <></>
  );
};

const emptyFloors: FloorSelectItem[] = [];

export const toFloorSelectItem = (
  floor: Floor | undefined,
): FloorSelectItem | undefined =>
  floor && {
    id: floor.floorId,
    value: floor,
  };

export const toFloor = (item: FloorSelectItem | undefined): Floor | undefined =>
  item?.value;

export const FloorSelect: FC<FloorSelectProps> = ({
  buildingId,
  floor,
  onChange,
  makeOption = defaultMakeOption,
  filterOptions = truphy,
  ...rest
}) => {
  const [input, setInput] = useState('');

  const dispatch = useAppDispatch();
  const { floorsByBuilding, floorsById, loading } = useSelector(getFloorsState);

  const options = useMemo((): FloorSelectItem[] => {
    if (!buildingId) return emptyFloors;

    const ids = floorsByBuilding.get(buildingId);
    if (!ids) return emptyFloors;

    const result: FloorSelectItem[] = [];
    for (const id of ids) {
      const item = toFloorSelectItem(floorsById.get(id));
      if (item && filterOptions(item)) result.push(item);
    }
    return result;
  }, [buildingId, filterOptions, floorsByBuilding, floorsById]);

  const filteredOptions = useMemo((): FloorSelectItem[] => {
    const search = input.toLowerCase();

    return options.filter(option =>
      option.value.title.toLowerCase().includes(search),
    );
  }, [input, options]);

  useEffect(() => {
    if (!buildingId) return;

    dispatch(fetchFloors({ cached: true, buildingId }));
  }, [buildingId, dispatch]);

  const handleChange = useCallback(
    (nextValue: FloorSelectItem | undefined) => {
      onChange(nextValue);
    },
    [onChange],
  );

  const handleClear = useCallback(async () => {
    setInput('');
    handleChange(undefined);
  }, [handleChange]);

  return (
    <Autocomplete
      {...(rest as AutocompleteProps<FloorSelectItem>)}
      size='medium'
      label='Этаж'
      fullWidth
      placeholder='выберите этаж'
      noOptionsText='нет этажей'
      options={filteredOptions}
      loading={loading}
      renderValue={renderValue}
      makeOption={makeOption}
      multiple={false}
      value={floor ?? null}
      onChange={handleChange}
      onInputChange={setInput}
      onInputClear={handleClear}
    />
  );
};
